import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tabs, Tab, Paper } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { JsonViewer } from './JsonViewer';

const borderColor = '#30363d';
const accentColor = '#58a6ff';
const panelBg = 'rgba(22, 27, 34, 0.75)';

const GlassPaper = styled(Paper)({
  backgroundColor: panelBg,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${borderColor}`,
  borderRadius: '8px',
  overflow: 'hidden',
});

export const DetailPane = ({ selectedMsg, setSelectedId, sidebarWidth }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    setShowToken(false);
  }, [selectedMsg?.id]);

  return (
    <Box sx={{ width: `calc(${100 - sidebarWidth}% - 4px)`, display: 'flex', flexDirection: 'column', bgcolor: '#0d1117', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ borderBottom: `1px solid ${borderColor}`, bgcolor: panelBg }}>
        <Box sx={{ p: 2, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
            Request Details
          </Typography>
          <IconButton size='small' onClick={() => setSelectedId(null)} sx={{ color: '#8b949e' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Tabs
          value={tabIndex}
          onChange={(e, val) => setTabIndex(val)}
          sx={{
            px: 2,
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, textTransform: 'none', color: '#8b949e', '&.Mui-selected': { color: accentColor } },
            '& .MuiTabs-indicator': { backgroundColor: accentColor },
          }}
        >
          <Tab label='Headers' />
          <Tab label='Request' />
          <Tab label='Response' />
        </Tabs>
      </Box>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Headers Section */}
        {tabIndex === 0 && (
          <GlassPaper sx={{ p: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1, alignItems: 'center' }}>
              <Typography variant='caption' sx={{ color: '#8b949e' }}>
                Resource
              </Typography>
              <Typography variant='body2' sx={{ wordBreak: 'break-all' }}>
                {selectedMsg?.resource || '-'}
              </Typography>

              <Typography variant='caption' sx={{ color: '#8b949e' }}>
                Trigger Page
              </Typography>
              <Typography variant='body2' sx={{ wordBreak: 'break-all', color: '#58a6ff' }}>
                {selectedMsg?.triggerUrl || '-'}
              </Typography>

              <Typography variant='caption' sx={{ color: '#8b949e' }}>
                Request ID
              </Typography>
              <Typography variant='body2'>{selectedMsg?.id}</Typography>

              <Typography variant='caption' sx={{ color: '#8b949e' }}>
                Method
              </Typography>
              <Typography variant='body2'>{selectedMsg?.method || '-'}</Typography>

              <Typography variant='caption' sx={{ color: '#8b949e' }}>
                Access Token
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant='body2'
                  onClick={() => setShowToken(!showToken)}
                  sx={{
                    wordBreak: 'break-all',
                    color: '#3fb950',
                    cursor: 'pointer',
                    fontFamily: showToken ? 'inherit' : 'monospace',
                    userSelect: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {selectedMsg?.params?.accessToken ? (showToken ? selectedMsg.params.accessToken : '••••••••••••••••••••') : '-'}
                </Typography>
                {selectedMsg?.params?.accessToken && (
                  <Typography
                    variant='caption'
                    onClick={() => setShowToken(!showToken)}
                    sx={{ color: '#8b949e', cursor: 'pointer', userSelect: 'none', fontSize: '10px', textDecoration: 'underline' }}
                  >
                    {showToken ? '(hide)' : '(show)'}
                  </Typography>
                )}
              </Box>
            </Box>
          </GlassPaper>
        )}

        {/* Request Payload */}
        {tabIndex === 1 && (
          <GlassPaper sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {selectedMsg?.params ? (
              <JsonViewer data={selectedMsg.params} title='request_parameters.json' />
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography variant='body2' sx={{ color: '#8b949e', fontStyle: 'italic' }}>
                  No parameters provided
                </Typography>
              </Box>
            )}
          </GlassPaper>
        )}

        {/* Response Payload */}
        {tabIndex === 2 && (
          <GlassPaper sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {selectedMsg?.status === 'pending' ? (
              <Box sx={{ p: 2 }}>
                <Typography variant='body2' sx={{ color: '#8b949e', fontStyle: 'italic' }}>
                  Awaiting response...
                </Typography>
              </Box>
            ) : selectedMsg?.error ? (
              <JsonViewer data={selectedMsg.error} title='response_error.json' />
            ) : selectedMsg?.result ? (
              <JsonViewer data={selectedMsg.result.data || selectedMsg.result} title='response_data.json' />
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography variant='body2' sx={{ color: '#8b949e', fontStyle: 'italic' }}>
                  Empty response
                </Typography>
              </Box>
            )}
          </GlassPaper>
        )}
      </Box>
    </Box>
  );
};
